import type { ChartDataPoint, TopPerformerInfo, Performer, ActiveUsersResponse } from '../types';

/**
 * Maps game history API response to ChartDataPoint array
 */
export const mapGameHistoryResponse = (result: any): ChartDataPoint[] => {
  if (!result?.dataPoints) return [];

  return result.dataPoints.map((point: any) => ({
    label: point.label,
    date: point.date,
    // Started counts
    daily: point.dailyChallenge || 0,
    dailyCompleted: point.dailyChallengeCompleted || 0,
    dailyFailed: point.dailyChallengeFailed || 0,
    vsRandom: point.vsRandom || 0,
    vsFriends: point.vsFriends || 0,
    vsBot: point.vsBot || 0,
    single: point.singleMode || 0,
    // Total VS games from DailyActivityLog (or GameBoard for today)
    vsGamesStarted: point.vsGamesStarted || 0,
    vsGamesCompleted: point.vsGamesCompleted || 0,
    // Completed counts
    vsRandomCompleted: point.vsRandomCompleted || 0,
    vsFriendsCompleted: point.vsFriendsCompleted || 0,
    vsBotCompleted: point.vsBotCompleted || 0,
    singleCompleted: point.singleModeCompleted || 0,
    onlineUsers: point.onlineUsers || 0,
  }));
};

/**
 * Maps top performers API response to Performer array
 */
export const mapTopPerformersResponse = (result: any): Performer[] => {
  if (!result?.success) return [{ name: 'No data', mode: '-', wins: 0 }];

  const performers: Performer[] = [];

  if (result.vsModeTop) {
    performers.push({
      name: result.vsModeTop.username || 'Unknown',
      mode: 'VS Mode',
      wins: result.vsModeTop.wins,
      totalPlays: result.vsModeTop.totalPlays,
      userId: result.vsModeTop.userId,
      rankName: result.vsModeTop.rankName,
    });
  }

  if (result.dailyChallengeTop) {
    performers.push({
      name: result.dailyChallengeTop.username || 'Unknown',
      mode: 'Daily Challenge',
      wins: result.dailyChallengeTop.wins,
      totalPlays: result.dailyChallengeTop.totalPlays,
      userId: result.dailyChallengeTop.userId,
      rankName: result.dailyChallengeTop.rankName,
    });
  }

  if (result.singleModeTop) {
    performers.push({
      name: result.singleModeTop.username || 'Unknown',
      mode: 'Single Mode',
      wins: result.singleModeTop.wins,
      totalPlays: result.singleModeTop.totalPlays,
      userId: result.singleModeTop.userId,
      rankName: result.singleModeTop.rankName,
    });
  }

  if (result.botModeTop) {
    performers.push({
      name: result.botModeTop.username || 'Unknown',
      mode: 'VS Bot',
      wins: result.botModeTop.wins,
      totalPlays: result.botModeTop.totalPlays,
      userId: result.botModeTop.userId,
      rankName: result.botModeTop.rankName,
    });
  }

  return performers.length > 0 ? performers : [{ name: 'No data', mode: '-', wins: 0 }];
};

/**
 * Maps top performers by mode API response to Performer array
 */
export const mapTopPerformersByModeResponse = (result: any): Performer[] => {
  if (!result?.success || !result.performers) return [];
  return result.performers.map((p: any) => ({
    name: p.username || 'Unknown',
    mode: result.mode || '',
    wins: p.wins,
    totalPlays: p.totalPlays,
    userId: p.userId,
    rankName: p.rankName,
  }));
};

/**
 * Maps active users API response
 */
export const mapActiveUsersResponse = (result: any): ActiveUsersResponse => {
  if (!result?.success) {
    return { success: false, users: [], totalCount: 0, page: 1, pageSize: 20 };
  }
  return {
    success: true,
    users: (result.users || []).map((u: any) => ({
      userId: u.userId,
      username: u.username || 'Unknown',
      rankName: u.rankName,
      lastActivityTime: u.lastActivityTime || null,
      vsGames: u.vsGames || 0,
      botGames: u.botGames || 0,
      singleGames: u.singleGames || 0,
      dailyChallengeGames: u.dailyChallengeGames || 0,
      totalGamesPlayed: u.totalGamesPlayed || 0,
    })),
    totalCount: result.totalCount || 0,
    page: result.page || 1,
    pageSize: result.pageSize || 20,
  };
};

/**
 * Calculate percentage change between two values
 */
export const calculatePercentageChange = (previous: number, current: number): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100 * 100) / 100;
};
